/**
 * BBS Weekender Live Personalization Preview
 * Product: Diamond Classic Weekender (/products/diamond-classic-weekender)
 *
 * Behaviour:
 *  • When the customer types their first initial, the gallery auto-slides to
 *    the blank bag image (last slide) and the canvas preview appears.
 *  • The overlay only shows while the last slide is active — user can freely
 *    swipe to other slides and come back.
 *  • Clearing the field hides the overlay and resets navigation.
 */
(function () {
  'use strict';

  if (window.location.pathname.indexOf('diamond-classic-weekender') === -1) return;

  /* ── Calibrated config (2026-07-01) ─────────────────────────────────── */
  var LEFT_CX_FRAC    = 0.4623;
  var LEFT_CY_FRAC    = 0.4694;
  var LEFT_SIZE_FRAC  = 0.0638;
  var RIGHT_CX_FRAC   = 0.5470;
  var RIGHT_CY_FRAC   = 0.4672;
  var RIGHT_SIZE_FRAC = 0.0638;
  var LETTER_FONT     = '"Courier New", Courier, monospace';
  var LETTER_COLOR    = '#e4e2dd';

  /* Blank bag — last gallery slide */
  var CLEAN_SRC = 'https://cdn.shopify.com/s/files/1/0370/6771/8794/files/new_BBS_4.png?v=1782921879';

  /* ── State ───────────────────────────────────────────────────────────── */
  var inputEl = null;
  var flkty   = null;   // Flickity instance
  var lastIdx = 0;      // index of blank-bag slide
  var overlay = null;
  var canvas  = null;
  var ctx     = null;
  var showing = false;
  var didNav  = false;  // have we auto-navigated this input session?

  var bagImg    = new Image();
  var bagLoaded = false;
  bagImg.crossOrigin = 'anonymous';
  bagImg.onload  = function () { bagLoaded = true; repaint(); };
  bagImg.onerror = function () {
    /* retry without crossOrigin */
    var fb = new Image();
    fb.onload = function () { bagImg = fb; bagLoaded = true; repaint(); };
    fb.src = CLEAN_SRC;
  };
  bagImg.src = CLEAN_SRC;

  /* ── Overlay: covers the full gallery viewport ───────────────────────── */
  function buildOverlay(gallery) {
    if (overlay) return;
    if (getComputedStyle(gallery).position === 'static') gallery.style.position = 'relative';

    overlay = document.createElement('div');
    overlay.id = 'bbs-weekender-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText = [
      'position:absolute', 'top:0', 'left:0', 'right:0', 'bottom:0',
      'z-index:20', 'opacity:0', 'transition:opacity .22s ease',
      'pointer-events:none', 'overflow:hidden',
    ].join(';');

    canvas = document.createElement('canvas');
    canvas.style.cssText = 'display:block;width:100%;height:100%;';
    overlay.appendChild(canvas);
    gallery.appendChild(overlay);
    ctx = canvas.getContext('2d');
  }

  /* ── Flickity helpers ────────────────────────────────────────────────── */
  function resolveFlickity(root) {
    if (!window.Flickity) return null;
    /* Turbo sometimes puts the Flickity element on a child, not the root */
    var candidates = [root].concat(
      Array.prototype.slice.call(root.querySelectorAll('[data-flickity],.flickity-enabled'))
    );
    for (var i = 0; i < candidates.length; i++) {
      var fl = window.Flickity.data(candidates[i]);
      if (fl) return fl;
    }
    return null;
  }

  function goToLast() {
    if (didNav || !flkty) return;
    didNav = true;
    flkty.select(lastIdx, false, false); /* animate to blank-bag slide */
  }

  /* ── Visibility ──────────────────────────────────────────────────────── */
  function setVisible(v) {
    if (!overlay) return;
    overlay.style.opacity = v ? '1' : '0';
    showing = v;
  }

  /* ── Render ──────────────────────────────────────────────────────────── */
  function repaint() {
    if (!canvas || !ctx || !bagLoaded) return;

    var raw = inputEl ? inputEl.value.trim().toUpperCase().replace(/[^A-Z]/g, '') : '';
    var L = raw.charAt(0);
    var R = raw.charAt(1);

    /* Input cleared → hide and reset so next keystroke re-navigates */
    if (!L) {
      ctx.clearRect(0, 0, canvas.width || 1, canvas.height || 1);
      setVisible(false);
      didNav = false;
      return;
    }

    /* First character typed → slide to blank bag */
    goToLast();

    /* Only paint while the last slide is the active one */
    var onLast = !flkty || (flkty.selectedIndex === lastIdx);
    if (!onLast) { setVisible(false); return; }

    /* Size canvas to physical pixels */
    var rect = canvas.getBoundingClientRect();
    var dpr  = window.devicePixelRatio || 1;
    var W = Math.round(rect.width  * dpr) || 600;
    var H = Math.round(rect.height * dpr) || 600;
    if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H; }

    ctx.clearRect(0, 0, W, H);

    /* Cover-fill the clean bag image */
    var iW = bagImg.naturalWidth  || W;
    var iH = bagImg.naturalHeight || H;
    var sc = (iW / iH > W / H) ? H / iH : W / iW;
    var dW = iW * sc, dH = iH * sc;
    var dX = (W - dW) / 2, dY = (H - dH) / 2;
    ctx.drawImage(bagImg, dX, dY, dW, dH);

    /* Initials — size calibrated against shorter image dimension */
    var ref = Math.min(dW, dH);
    drawLetter(dX + LEFT_CX_FRAC  * dW, dY + LEFT_CY_FRAC  * dH, L, ref * LEFT_SIZE_FRAC  * 0.70);
    drawLetter(dX + RIGHT_CX_FRAC * dW, dY + RIGHT_CY_FRAC * dH, R, ref * RIGHT_SIZE_FRAC * 0.70);

    setVisible(true);
  }

  function drawLetter(x, y, ch, fs) {
    if (!ch) return;
    ctx.save();
    ctx.font            = fs + 'px ' + LETTER_FONT;
    ctx.fillStyle       = LETTER_COLOR;
    ctx.textAlign       = 'center';
    ctx.textBaseline    = 'middle';
    ctx.shadowColor     = 'rgba(0,0,0,0.10)';
    ctx.shadowBlur      = Math.max(1, fs * 0.04);
    ctx.fillText(ch, x, y);
    ctx.restore();
  }

  /* ── Find the BOLD product-options input ─────────────────────────────── */
  function findInput() {
    var selectors = [
      'input[name="properties[Initials]"]',
      'input[name="properties[initials]"]',
      'input[name="properties[Monogram]"]',
      'input[name="properties[monogram]"]',
      'input[name="properties[Personalization]"]',
      'input[name="properties[Letters]"]',
      'input[name="properties[Name]"]',
    ];
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (el) return el;
    }
    /* Fallback: match by label text */
    var found = null;
    document.querySelectorAll('label').forEach(function (lbl) {
      if (found) return;
      var t = lbl.textContent.toLowerCase();
      if (t.indexOf('initial') > -1 || t.indexOf('monogram') > -1 || t.indexOf('letter') > -1) {
        found = lbl.control || document.getElementById(lbl.htmlFor);
      }
    });
    return found;
  }

  /* ── Init ────────────────────────────────────────────────────────────── */
  function init() {
    var gallery = document.querySelector('[data-product-gallery]')
      || document.querySelector('.product_gallery')
      || document.querySelector('.product-gallery')
      || document.querySelector('[class*="product"][class*="gallery"]');
    if (!gallery) return;

    buildOverlay(gallery);

    /* Resolve Flickity instance (takes ~200–600 ms after DOMContentLoaded) */
    var fTries = 0;
    var fPoll = setInterval(function () {
      flkty = resolveFlickity(gallery);
      if (flkty) {
        clearInterval(fPoll);
        lastIdx = flkty.slides.length - 1;

        /* Hide overlay when user swipes away from the blank-bag slide */
        flkty.on('change', function (idx) {
          if (!inputEl) return;
          var raw = inputEl.value.trim().toUpperCase().replace(/[^A-Z]/g, '');
          if (!raw) return;
          if (idx === lastIdx) repaint(); /* came back → redraw */
          else setVisible(false);          /* left → hide    */
        });
      } else if (++fTries > 40) {
        clearInterval(fPoll);
        /* No Flickity: overlay will just always show when typing */
      }
    }, 100);

    /* Inject mobile-only preview hint (once) */
    var hintStyle = document.createElement('style');
    hintStyle.textContent = '#bbs-preview-hint{display:none}@media(max-width:767px){#bbs-preview-hint{display:block;font-size:11px;color:#888;margin-top:5px;letter-spacing:.01em}}';
    document.head.appendChild(hintStyle);

    /* Poll for BOLD input (injected dynamically up to ~6 s) */
    var iTries = 0;
    var iPoll = setInterval(function () {
      inputEl = findInput();
      if (inputEl) {
        clearInterval(iPoll);
        inputEl.addEventListener('input',  repaint);
        inputEl.addEventListener('change', repaint);
        setTimeout(repaint, 150); /* paint if already pre-filled */

        /* Insert hint below the input's parent wrapper */
        if (!document.getElementById('bbs-preview-hint')) {
          var hint = document.createElement('p');
          hint.id = 'bbs-preview-hint';
          hint.textContent = '★ Preview personalization above';
          var insertAfter = inputEl.closest('.bold_option, .product-form__input, .field, [class*="option"]') || inputEl.parentNode;
          insertAfter.parentNode.insertBefore(hint, insertAfter.nextSibling);
        }
      } else if (++iTries > 80) clearInterval(iPoll);
    }, 100);

    window.addEventListener('resize', function () { if (showing) repaint(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(init, 400); });
  } else {
    setTimeout(init, 400);
  }

})();
